import { type RefObject, useCallback, useEffect } from 'react'

/**
 * Hook to detect clicks outside a referenced element.
 * Uses mousedown for immediate detection.
 *
 * @param ref - Reference to the element to monitor
 * @param handler - Callback when click outside occurs
 * @param enabled - Whether the hook is active (default: true)
 * @param excludeRefs - Additional refs to exclude from outside detection (e.g., portaled elements)
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: MouseEvent) => void,
  enabled: boolean = true,
  excludeRefs?: RefObject<HTMLElement | null>[]
): void {
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node
      if (ref.current && !ref.current.contains(target)) {
        const isInExcluded = excludeRefs?.some(
          (excludeRef) => excludeRef.current?.contains(target)
        )
        if (!isInExcluded) {
          handler(event)
        }
      }
    },
    [ref, handler, excludeRefs]
  )

  useEffect(() => {
    if (!enabled) return

    // Use mousedown for immediate detection (before click completes)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside, enabled])
}
